/**
 * The shared header row of Home's three views. The Briefing / Dashboard /
 * By Type switch always sits in the middle cell of a 1fr / auto / 1fr grid, so
 * it stays at the same spot whichever view is selected and whatever the start
 * and end groups show. When the pane is too narrow for three columns, the
 * switch drops onto its own centered row (the same in every view).
 */
import type { FC, ReactNode } from "react";

import { cn } from "@/lib/utils";

type Props = {
  /** Left-aligned group (the greeting, the briefing title, the hint). */
  start?: ReactNode;
  /** The view switch. */
  center: ReactNode;
  /** Right-aligned group (last updated, scope, display, arrows). */
  end?: ReactNode;
  className?: string;
  testId?: string;
  children?: never;
};

export const HomeHeaderRow: FC<Props> = ({
  start,
  center,
  end,
  className,
  testId = "home-header-row"
}) => (
  <div className="@container w-full min-w-0 shrink-0" data-testid={testId}>
    <div
      className={cn(
        "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2.5 gap-y-2 @5xl:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]",
        className
      )}
    >
      <div
        className="col-start-1 row-start-1 flex min-w-0 items-center justify-start gap-2"
        data-testid="home-header-start"
      >
        {start}
      </div>
      <div
        className="col-span-2 col-start-1 row-start-2 flex items-center justify-center @5xl:col-span-1 @5xl:col-start-2 @5xl:row-start-1"
        data-testid="home-header-center"
      >
        {center}
      </div>
      <div
        className="col-start-2 row-start-1 flex min-w-0 items-center justify-end gap-2 @5xl:col-start-3"
        data-testid="home-header-end"
      >
        {end}
      </div>
    </div>
  </div>
);
